import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { BUILD_INFO } from '../../build-info';

/**
 * SubmitDiagnosticsService — client-side instrumentation for form submissions.
 *
 * Purpose: characterize the `no-account` auth failures on the JSA (and the other
 * five public safety forms) by capturing, at submit time, the browser/MSAL/nav
 * state and how the form was filled (typed vs. pasted vs. autofilled/restored).
 * See routes/submitDiagApi.js + schemas/submitDiagnostics.js in the API repo.
 *
 * Design notes:
 *  - METADATA ONLY. This never reads or transmits form field VALUES — only
 *    counts, timings, and event kinds. Keep it that way.
 *  - Transport is navigator.sendBeacon with a text/plain Blob. Beacons survive
 *    page unload and — being a CORS-safelisted content type — dodge the preflight
 *    that a custom header or application/json would require (the 2026-07-13
 *    footgun). The form POST itself is left 100% untouched; we never add a header
 *    or body to it. Beacon and rejectedSubmissions rows correlate by
 *    clientIp + form + timestamp.
 *  - Fire-and-forget. Any failure here must never affect the app or the form
 *    submission, so everything is wrapped defensively.
 */

// The six public form-submission endpoints, keyed by their API path segment.
// Segment === the `form` token persisted by the backend (jsa | jsapaper | ...).
const FORM_SEGMENTS = ['jsa', 'jsapaper', 'nearmiss', 'goodcatch', 'accident', 'sse'] as const;
export type DiagFormName = (typeof FORM_SEGMENTS)[number];

// Per-form accumulator of entry/click behavior for the current page-life.
interface FormStats {
  firstInputMs?: number;
  lastInputMs?: number;
  activeTypeMs: number;
  lastInputAt?: number;   // performance.now() of previous input, for active-time gaps
  typed: number;
  pasted: number;
  replaced: number;
  clickCount: number;
  clickGaps: number[];
  lastClickAt?: number;   // performance.now() of previous submit click
  lastInputPerfAt?: number; // performance.now() of last input, for idle-before-click
  disabledWhenClicked?: boolean;
  trusted?: boolean;
}

// Outcome of the interceptor's token-acquisition attempt for a monitored POST.
// 'ok' = a Bearer was attached; the rest mirror the X-Auth-Fail reasons the
// interceptor stamps on token-less requests. 'interaction-required' is the C3
// discriminator (account present + indexed, but acquireTokenSilent needs interaction).
export type AcquireStatus =
  | 'ok'
  | 'no-account'
  | 'interaction-required'
  | 'timeout'
  | 'silent-error';

// Snapshot captured at request time and finalized when the response outcome is known.
export interface DiagSnapshot {
  form: DiagFormName;
  sid: string;
  base: Record<string, unknown>;
  // Set by the AuthInterceptor once token acquisition resolves, before reportOutcome.
  acquireStatus?: AcquireStatus;
}

// Gaps larger than this between consecutive inputs are treated as "away from
// keyboard" and not counted toward active editing time.
const ACTIVE_GAP_CAP_MS = 2000;

@Injectable({ providedIn: 'root' })
export class SubmitDiagnosticsService {
  private readonly stats = new Map<DiagFormName, FormStats>();

  // Page-life lifecycle counters.
  private hiddenCount = 0;
  private hiddenMs = 0;
  private hiddenSince?: number;
  private bfRestore = false;

  private incognitoGuess: boolean | undefined = undefined;

  constructor(private msalService: MsalService) {}

  /** Registers page-lifecycle listeners. Called once from an APP_INITIALIZER. */
  init(): void {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenSince = performance.now();
      } else if (this.hiddenSince !== undefined) {
        this.hiddenCount++;
        this.hiddenMs += performance.now() - this.hiddenSince;
        this.hiddenSince = undefined;
      }
    });

    window.addEventListener('pageshow', (e) => {
      if ((e as PageTransitionEvent).persisted) {
        this.bfRestore = true;
      }
    });

    this.detectIncognito();
  }

  // ---- Called by the appSubmitDiag directive (capture-phase form listeners) ----

  recordInput(form: DiagFormName, inputType: string | null): void {
    try {
      const s = this.ensure(form);
      const now = performance.now();
      if (s.firstInputMs === undefined) s.firstInputMs = Math.round(now);
      s.lastInputMs = Math.round(now);
      if (s.lastInputAt !== undefined) {
        const gap = now - s.lastInputAt;
        if (gap > 0 && gap <= ACTIVE_GAP_CAP_MS) s.activeTypeMs += gap;
      }
      s.lastInputAt = now;
      s.lastInputPerfAt = now;
      switch (inputType) {
        case 'insertFromPaste':
          s.pasted++;
          break;
        case 'insertReplacementText':
          s.replaced++;
          break;
        default:
          // insertText and the various composition/delete kinds all count as typing.
          s.typed++;
      }
    } catch {
      // never let instrumentation throw into the app
    }
  }

  recordSubmitClick(form: DiagFormName, isTrusted: boolean, disabled: boolean): void {
    try {
      const s = this.ensure(form);
      const now = performance.now();
      if (s.lastClickAt !== undefined) s.clickGaps.push(Math.round(now - s.lastClickAt));
      s.lastClickAt = now;
      s.clickCount++;
      s.trusted = isTrusted;
      s.disabledWhenClicked = disabled;
    } catch {
      // swallow
    }
  }

  // ---- Called by the AuthInterceptor ----

  /** Maps a request to a monitored form segment, or null if not a form POST. */
  formForUrl(method: string, url: string): DiagFormName | null {
    if (method !== 'POST') return null;
    const base = environment.apiConfig.uri;
    for (const seg of FORM_SEGMENTS) {
      const target = `${base}/${seg}`;
      if (url === target || url.startsWith(`${target}?`)) return seg;
    }
    return null;
  }

  /** Captures browser/MSAL/nav/entry state at request time (before the response). */
  snapshot(form: DiagFormName): DiagSnapshot {
    let base: Record<string, unknown> = {};
    try {
      base = {
        tab: this.tabId(),
        form,
        build: BUILD_INFO.sha,
        nav: this.navState(),
        vis: { hiddenCount: this.hiddenCount, hiddenMs: Math.round(this.hiddenMs) },
        msal: this.msalState(),
        enc: this.encState(),
        net: this.netState(),
        env: this.envState(),
        click: this.clickState(form),
        entry: this.entryState(form),
      };
    } catch {
      base = { form };
    }
    return { form, sid: this.uuid(), base };
  }

  /**
   * Finalizes and sends a beacon once the HTTP outcome is known. Sends all
   * failures; samples successes at environment.diagConfig.successSampleRate.
   */
  reportOutcome(snap: DiagSnapshot, httpStatus: number): void {
    try {
      const cfg = (environment as any).diagConfig as
        | { enabled?: boolean; successSampleRate?: number }
        | undefined;
      if (!cfg?.enabled) return;

      const outcome: 'success' | 'failure' = httpStatus >= 200 && httpStatus < 300 ? 'success' : 'failure';
      const sampled = outcome === 'success';
      if (sampled) {
        const rate = typeof cfg.successSampleRate === 'number' ? cfg.successSampleRate : 0;
        if (Math.random() >= rate) return;
      }

      const payload = {
        sid: snap.sid,
        ...snap.base,
        outcome,
        httpStatus,
        sampled,
        ...(snap.acquireStatus ? { acquireStatus: snap.acquireStatus } : {}),
      };
      this.send(payload);
    } catch {
      // swallow
    }
  }

  // ---- internals ----

  private send(payload: Record<string, unknown>): void {
    try {
      if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
      const url = `${environment.apiConfig.uri}/submit-diag`;
      const body = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
      navigator.sendBeacon(url, body);
    } catch {
      // swallow — diagnostics must never disrupt the app
    }
  }

  private ensure(form: DiagFormName): FormStats {
    let s = this.stats.get(form);
    if (!s) {
      s = { activeTypeMs: 0, typed: 0, pasted: 0, replaced: 0, clickCount: 0, clickGaps: [] };
      this.stats.set(form, s);
    }
    return s;
  }

  private tabId(): string {
    try {
      const key = 'lp.diag.tab';
      let id = sessionStorage.getItem(key);
      if (!id) {
        id = this.uuid();
        sessionStorage.setItem(key, id);
      }
      return id;
    } catch {
      return 'no-session-storage';
    }
  }

  private navState(): Record<string, unknown> {
    const out: Record<string, unknown> = { sinceNavMs: Math.round(performance.now()) };
    try {
      const nav = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (nav) out['type'] = nav.type;
      out['wasDiscarded'] = (document as any).wasDiscarded === true;
      out['bfRestore'] = this.bfRestore;
    } catch {
      // partial nav state is fine
    }
    return out;
  }

  private msalState(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    try {
      const instance = this.msalService.instance;
      const accounts = instance.getAllAccounts().length;
      const cachedAccounts = this.cachedAccountCount();
      out['accounts'] = accounts;
      out['lsKeys'] = this.hasMsalLocalStorageKeys();
      // Coarse A/B discriminator for the `no-account` failure (accounts === 0), kept for
      // continuity with the historical time series:
      //   accounts === 0 && cachedAccounts > 0  → CASE A: account entries persisted but
      //     not served from the in-memory cache at submit.
      //   accounts === 0 && cachedAccounts === 0 → CASE B: no account persisted.
      // NOTE: cachedAccounts alone cannot tell whether a Case-A account is actually
      // *recoverable* — a persisted blob may be encrypted with a dead (previous-session)
      // key and thus undecryptable. Use the `enc` block (encStale/idx/plain/cookie) for
      // the real mechanism split; see encState().
      out['cachedAccounts'] = cachedAccounts;
      out['recoverable'] = accounts === 0 && cachedAccounts > 0;
    } catch {
      // swallow
    }
    return out;
  }

  private hasMsalLocalStorageKeys(): boolean {
    try {
      return Object.keys(localStorage).some((k) => k.includes('login.windows.net') || k.includes('-login.microsoftonline.com') || k.startsWith('msal.'));
    } catch {
      return false;
    }
  }

  /**
   * Counts MSAL *account* entries actually persisted in the browser — as opposed to
   * access/refresh/id-token, interaction-status, and telemetry keys, which also live
   * under the authority host and are what make `hasMsalLocalStorageKeys()` a weak
   * signal. An MSAL account entry is a JSON object carrying authorityType +
   * homeAccountId + a user identifier and, unlike credential entries, no
   * credentialType. This is the discriminator between case A (in-memory cache empty
   * but a real account persisted → silently recoverable) and case B (only orphaned
   * keys → session truly gone).
   *
   * Scans BOTH localStorage and sessionStorage because MSAL's cacheLocation differs
   * by environment (prod = LocalStorage, dev/local = SessionStorage). An account lives
   * in exactly one of the two, so summing them cannot double-count and makes the
   * signal correct regardless of environment (and resilient to a future cacheLocation
   * change).
   *
   * Read-only by design: it never calls initialize() or otherwise mutates MSAL state,
   * so it cannot perturb the live submission it is measuring.
   */
  private cachedAccountCount(): number {
    return this.countAccountsIn(localStorage) + this.countAccountsIn(sessionStorage);
  }

  private countAccountsIn(store: Storage): number {
    let count = 0;
    try {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (!key) continue;
        // Account entries live under the authority host; skip everything else cheaply.
        if (!key.includes('login.windows.net') && !key.includes('login.microsoftonline.com')) continue;
        const raw = store.getItem(key);
        if (!raw || raw.charCodeAt(0) !== 0x7b /* '{' */) continue;
        let obj: any;
        try {
          obj = JSON.parse(raw);
        } catch {
          continue;
        }
        if (
          obj &&
          obj.authorityType &&
          obj.homeAccountId &&
          (obj.username || obj.localAccountId) &&
          !obj.credentialType
        ) {
          count++;
        }
      }
    } catch {
      // partial/blocked storage — return what we counted
    }
    return count;
  }

  /**
   * Encryption-cache probe — targeted at the `accounts:0` (no-account) failure to
   * decide WHY MSAL is serving zero accounts despite persisted cache.
   *
   * MSAL v5's LocalStorage backend (prod cacheLocation) encrypts account/token
   * entities at rest. The decryption key lives in a SESSION-SCOPED cookie
   * (`msal.cache.encryption`, expiration 0 → cleared at browser-session end). On the
   * next load MSAL mints a NEW key with a new id and DISCARDS any blob whose embedded
   * id no longer matches, removing it from localStorage (see LocalStorage.mjs
   * importArray/getItemFromEncryptedCache in @azure/msal-browser). This probe
   * distinguishes the competing root causes of an `accounts:0` submit:
   *   - encStale > 0          → account blobs persisted but encrypted with a dead
   *                             (previous-session) key → NOT silently recoverable;
   *                             confirms the cookie-lifetime hypothesis.
   *   - idx > 0 && accounts:0 → account is in the v5 index but not hydrated in-memory
   *                             → a genuine hydration miss (recoverable candidate).
   *   - plain > 0 && idx:0    → orphaned plaintext account entries not in the v5 index
   *                             (pre-v5 leftovers / kmsi) → cache litter, not a session.
   *   - cookie:false          → encryption-key cookie could not be persisted
   *                             (blocked / ITP eviction) → every load starts keyless.
   *
   * Read-only: never calls initialize() or mutates MSAL/localStorage, so it cannot
   * perturb the live submission it measures. Scans localStorage only (the encrypted
   * backend); the `key` field of the cookie (the actual key material) is NEVER read.
   */
  private encState(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    try {
      const cookieId = this.encryptionCookieId();
      out['cookie'] = cookieId !== null;
      if (cookieId) out['cookieId'] = cookieId;
      out['idx'] = this.accountIndexLength('msal.3.account.keys');
      out['idxLegacy'] = this.accountIndexLength('msal.account.keys');

      let plain = 0;
      let encd = 0;
      let encStale = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        // Account entries live under the authority host.
        if (!key.includes('login.windows.net') && !key.includes('login.microsoftonline.com')) continue;
        // Exclude credential, appmetadata, and index/meta keys — count accounts only.
        if (/-(accesstoken|idtoken|refreshtoken)-/.test(key)) continue;
        if (key.includes('appmetadata')) continue;
        if (key.includes('.keys')) continue;
        const raw = localStorage.getItem(key);
        if (!raw || raw.charCodeAt(0) !== 0x7b /* '{' */) continue;
        let obj: any;
        try {
          obj = JSON.parse(raw);
        } catch {
          continue;
        }
        if (obj && typeof obj.id === 'string' && obj.nonce && obj.data) {
          // Encrypted-at-rest blob shape: { id, nonce, data, lastUpdatedAt }.
          encd++;
          if (cookieId !== null && obj.id !== cookieId) encStale++;
        } else if (obj && obj.authorityType && obj.homeAccountId) {
          plain++;
        }
      }
      out['plain'] = plain;
      out['encd'] = encd;
      out['encStale'] = encStale;
    } catch {
      // partial/blocked storage — return what we have
    }
    return out;
  }

  /**
   * Returns the id (a random GUID) of the MSAL encryption-key cookie, or null if the
   * cookie is absent. Deliberately returns ONLY the id — never the `key` field, which
   * is the actual encryption key material and must never leave the browser.
   */
  private encryptionCookieId(): string | null {
    try {
      const name = 'msal.cache.encryption=';
      for (const part of document.cookie.split(';')) {
        const c = part.trim();
        if (c.startsWith(name)) {
          const parsed = JSON.parse(decodeURIComponent(c.substring(name.length)));
          return typeof parsed.id === 'string' ? parsed.id : '';
        }
      }
    } catch {
      // fall through
    }
    return null;
  }

  private accountIndexLength(key: string): number {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return 0;
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  }

  private netState(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    try {
      const c = (navigator as any).connection;
      if (c) {
        out['effectiveType'] = c.effectiveType;
        out['rtt'] = c.rtt;
        out['downlink'] = c.downlink;
        out['saveData'] = c.saveData;
      }
      const dm = (navigator as any).deviceMemory;
      if (typeof dm === 'number') out['deviceMemory'] = dm;
    } catch {
      // swallow
    }
    return out;
  }

  private envState(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    try {
      out['webview'] = this.detectWebview();
      out['cookies'] = navigator.cookieEnabled === true;
      if (this.incognitoGuess !== undefined) out['incognitoGuess'] = this.incognitoGuess;
    } catch {
      // swallow
    }
    return out;
  }

  private detectWebview(): string {
    try {
      const ua = navigator.userAgent || '';
      const tokens: Array<[RegExp, string]> = [
        [/FBAN|FBAV/, 'facebook'],
        [/Instagram/, 'instagram'],
        [/\bLine\//, 'line'],
        [/WhatsApp/, 'whatsapp'],
        [/\bGSA\//, 'google-app'],
        [/; wv\)/, 'android-webview'],
      ];
      for (const [re, name] of tokens) {
        if (re.test(ua)) return name;
      }
      return 'none';
    } catch {
      return 'none';
    }
  }

  private clickState(form: DiagFormName): Record<string, unknown> {
    const s = this.stats.get(form);
    if (!s) return {};
    const out: Record<string, unknown> = {
      count: s.clickCount,
      gaps: s.clickGaps.slice(0, 20),
    };
    if (s.disabledWhenClicked !== undefined) out['disabledWhenClicked'] = s.disabledWhenClicked;
    if (s.trusted !== undefined) out['trusted'] = s.trusted;
    if (s.lastInputPerfAt !== undefined && s.lastClickAt !== undefined) {
      out['idleBeforeMs'] = Math.max(0, Math.round(s.lastClickAt - s.lastInputPerfAt));
    }
    return out;
  }

  private entryState(form: DiagFormName): Record<string, unknown> {
    const s = this.stats.get(form);
    const typed = s?.typed ?? 0;
    const pasted = s?.pasted ?? 0;
    const replaced = s?.replaced ?? 0;
    const out: Record<string, unknown> = {
      typed,
      pasted,
      replaced,
      zeroInputFullForm: typed + pasted + replaced === 0,
    };
    if (s?.firstInputMs !== undefined) out['firstInputMs'] = s.firstInputMs;
    if (s?.lastInputMs !== undefined) out['lastInputMs'] = s.lastInputMs;
    if (s) out['activeTypeMs'] = Math.round(s.activeTypeMs);
    return out;
  }

  private async detectIncognito(): Promise<void> {
    try {
      const est = (navigator as any).storage?.estimate;
      if (typeof est === 'function') {
        const { quota } = await (navigator as any).storage.estimate();
        // Incognito sessions are typically capped well below normal quotas.
        if (typeof quota === 'number') this.incognitoGuess = quota < 120 * 1024 * 1024;
      }
    } catch {
      // leave undefined
    }
  }

  private uuid(): string {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch {
      // fall through
    }
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
